import { createContext, RefObject, useCallback, useContext, useRef, useState } from "react";
import { LOGS } from "../utils/logging";
import { Direction } from "../../config/globals";
import { eventInteractiveNavigateNull } from "../utils/gtag";

export const RemoteNavigationContext = createContext<{
	focusedId: string;
	handleKeyNavigation: (e: React.KeyboardEvent<HTMLElement>) => void;
	setFocus: (id: string) => void;
	registerElement: (id: string, focusable: RefObject<HTMLElement>) => void;
	unregisterElement: (id: string) => void;
}>({
	focusedId: "",
	handleKeyNavigation: () => {},
	setFocus: () => {},
	registerElement: () => {},
	unregisterElement: () => {},
});

type Focusables = Map<string, RefObject<HTMLElement | null>>;

export function useRemoteNavigation() {
	const context = RemoteNavigationContext;
	if (!context) {
		throw new Error("useRemoteNavigation must be used within a KeyboardNavigationProvider");
	}
	return useContext(context);
}

export function RemoteNavigationProvider({ children }: { children: React.ReactNode }) {
	const focusableElements = useRef<Focusables>(new Map());
	const [focusedId, setFocusedId] = useState<string>("");

	const setFocus = (id: string) => {
		LOGS.INTERACTIVE && console.info("setting focus to ID", id);

		setFocusedId(id);

		const focusableRef = focusableElements.current.get(id);

		if (!focusableRef || !focusableRef.current) {
			console.warn("Focus call skipped: focusable ref is null for", id);
			return;
		}
		focusableRef.current.focus();
	};

	const registerFocusable = (id: string, state: RefObject<HTMLElement>) => {
		focusableElements.current.set(id, state);
	};

	const unregisterFocusable = (id: string) => {
		focusableElements.current.delete(id);
	};

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLElement>) => {
			e.preventDefault();
			LOGS.INTERACTIVE && console.log("Registered keydown event");
			let nextFocusable = null;
			const focused = focusableElements.current.get(focusedId);
			if (!focused) {
				console.warn("no element currently focused");
				return;
			}
			const getNextFocusable = (direction: Direction) =>
				getElementInDirection(focusableElements.current, focusedId, direction);

			switch (e.key) {
				case "ArrowUp":
					nextFocusable = getNextFocusable("up");
					break;
				case "ArrowDown":
					nextFocusable = getNextFocusable("down");
					break;
				case "ArrowLeft":
					nextFocusable = getNextFocusable("left");
					break;
				case "ArrowRight":
					nextFocusable = getNextFocusable("right");
					break;
				case "Enter":
					if (!focused || !focused.current) {
						console.error("no focused found");
						return;
					}
					focused.current.click();
					return;
				case "Back":
					// TODO - Removing this for now because otherwise we need the RouterContext to have RemoteNavigationContext in root.
					return;
				default:
					return;
			}
			if (!nextFocusable) {
				eventInteractiveNavigateNull(focusedId, e.key);
				return;
			}
			setFocus(nextFocusable);
		},
		[focusedId],
	);

	const providerValue = {
		focusedId,
		setFocus: setFocus,
		handleKeyNavigation: onKeyDown,
		registerElement: registerFocusable,
		unregisterElement: unregisterFocusable,
	};

	return (
		<RemoteNavigationContext.Provider value={providerValue}>
			{children}
		</RemoteNavigationContext.Provider>
	);
}

function getElementInDirection(
	focusableElements: Focusables,
	id: string,
	direction: Direction,
): string | null {
	const currentFocusedElement = focusableElements.get(id);
	if (!currentFocusedElement || !currentFocusedElement.current) {
		console.warn("no element currently focused");
		return null;
	}
	const elementInDirectionID = currentFocusedElement.current.dataset[`${direction}`];

	const isValidElementID = elementInDirectionID && elementInDirectionID !== "null";
	if (!isValidElementID) return null;

	const focusable = focusableElements.get(elementInDirectionID);

	if (!focusable) {
		console.warn("element does not exist in focusable elements: ", elementInDirectionID);
		return null;
	}

	return elementInDirectionID;
}
